import { createTheme, alpha } from "@mui/material/styles";
import {
  FLUX_HEX,
  FLUX_CSS,
  INTERACTIVE_CARD_CLASS,
} from "./fluxTokens";

export { FLUX, STATUS_COLORS, SCORE_COLORS } from "./fluxTokens";
export type { ScoreLevel } from "./fluxTokens";

const { agentsAccent, success, warning, error, info, primary300, primary400 } =
  FLUX_HEX;

const theme = createTheme({
  spacing: 4,
  palette: {
    mode: "dark",
    primary: { main: agentsAccent, contrastText: FLUX_HEX.white },
    secondary: { main: primary400, contrastText: FLUX_HEX.white },
    background: { default: FLUX_HEX.background, paper: FLUX_HEX.paper },
    text: { primary: FLUX_HEX.textPrimary, secondary: FLUX_HEX.textSecondary },
    error: { main: error },
    warning: { main: warning },
    success: { main: success },
    info: { main: info },
    divider: FLUX_HEX.divider,
  },
  typography: {
    fontFamily: "var(--flux-font-sans)",
    h3: {
      fontSize: "var(--flux-text-4xl)",
      fontWeight: 700,
      lineHeight: 1.2,
      color: FLUX_CSS.heading,
    },
    h4: {
      fontSize: "var(--flux-text-3xl)",
      fontWeight: 700,
      lineHeight: 1.25,
      color: FLUX_CSS.heading,
    },
    h5: {
      fontSize: "var(--flux-text-2xl)",
      fontWeight: 700,
      lineHeight: 1.3,
      color: FLUX_CSS.heading,
    },
    h6: {
      fontSize: "var(--flux-text-xl)",
      fontWeight: 600,
      lineHeight: 1.4,
      color: FLUX_CSS.heading,
    },
    subtitle1: { fontSize: "var(--flux-text-lg)", fontWeight: 600 },
    subtitle2: {
      fontSize: "var(--flux-text-sm)",
      fontWeight: 600,
      lineHeight: 1.4,
      color: FLUX_CSS.textMuted,
    },
    body1: { fontSize: "var(--flux-text-base)", fontWeight: 400, lineHeight: 1.6 },
    body2: {
      fontSize: "var(--flux-text-sm)",
      fontWeight: 300,
      lineHeight: 1.6,
      color: FLUX_CSS.textMuted,
    },
    caption: { fontSize: "var(--flux-text-xs)", fontWeight: 300, lineHeight: 1.5 },
    button: { fontWeight: 600, letterSpacing: "0.02em" },
  },
  shape: { borderRadius: 12 },
  shadows: [
    "none",
    FLUX_CSS.shadowSm,
    FLUX_CSS.shadowSm,
    FLUX_CSS.shadowMd,
    FLUX_CSS.shadowMd,
    FLUX_CSS.shadowMd,
    FLUX_CSS.shadowMd,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowLg,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
    FLUX_CSS.shadowXl,
  ] as unknown as import("@mui/material/styles").Shadows,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: FLUX_CSS.background },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: FLUX_CSS.surface,
          border: `1px solid ${FLUX_CSS.border}`,
          boxShadow: FLUX_CSS.shadowMd,
          borderRadius: "var(--flux-radius-xl)",
          [`&.${INTERACTIVE_CARD_CLASS}`]: {
            transition:
              "border-color var(--flux-duration-normal) var(--flux-ease), box-shadow var(--flux-duration-normal) var(--flux-ease)",
            "&:hover": {
              borderColor: "var(--flux-primary-300)",
              boxShadow: FLUX_CSS.shadowLg,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "var(--flux-radius-md)",
          textTransform: "none" as const,
          fontWeight: 600,
        },
        contained: {
          boxShadow: FLUX_CSS.shadowSm,
          "&:hover": { boxShadow: FLUX_CSS.shadowMd },
        },
        outlined: {
          borderColor: FLUX_CSS.border,
          color: FLUX_CSS.text,
          "&:hover": {
            borderColor: FLUX_CSS.primary300,
            backgroundColor: alpha(agentsAccent, 0.06),
          },
        },
        text: {
          color: FLUX_CSS.text,
          "&:hover": {
            backgroundColor: alpha(agentsAccent, 0.06),
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          borderRadius: 9999,
          fontSize: "0.75rem",
        },
        sizeSmall: {
          height: 24,
          fontSize: "0.7rem",
        },
        colorSuccess: {
          backgroundColor: alpha(success, 0.15),
          color: success,
          border: "none",
        },
        colorWarning: {
          backgroundColor: alpha(warning, 0.15),
          color: warning,
          border: "none",
        },
        colorError: {
          backgroundColor: alpha(error, 0.15),
          color: error,
          border: "none",
        },
        colorInfo: {
          backgroundColor: alpha(info, 0.2),
          color: primary300,
          border: "none",
        },
        colorDefault: {
          backgroundColor: FLUX_CSS.border,
          color: FLUX_CSS.text,
          border: "none",
        },
        colorSecondary: {
          backgroundColor: alpha(primary400, 0.15),
          color: primary300,
          border: "none",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: FLUX_CSS.border },
        head: {
          fontWeight: 600,
          color: FLUX_CSS.textMuted,
          fontSize: "var(--flux-text-xs)",
          letterSpacing: "0.05em",
          textTransform: "uppercase" as const,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: FLUX_CSS.surface,
          borderRight: `1px solid ${FLUX_CSS.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: "var(--flux-radius-md)",
          margin: "2px 8px",
          "&.Mui-selected": {
            backgroundColor: alpha(agentsAccent, 0.15),
            color: agentsAccent,
            "&:hover": { backgroundColor: alpha(agentsAccent, 0.2) },
            "& .MuiListItemIcon-root": { color: agentsAccent },
          },
          "&.kira-nav-group": {
            minHeight: 44,
            "&:hover": {
              backgroundColor: alpha(agentsAccent, 0.06),
            },
            "&.Mui-selected": {
              backgroundColor: "transparent",
              color: "inherit",
            },
          },
          "&.kira-nav-child": {
            minHeight: 36,
            margin: "1px 0",
            paddingLeft: 12,
            "&.Mui-selected": {
              backgroundColor: alpha(agentsAccent, 0.12),
              boxShadow: `inset 3px 0 0 ${agentsAccent}`,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: "var(--flux-radius-md)",
            "& fieldset": { borderColor: FLUX_CSS.border },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: FLUX_CSS.surface,
          boxShadow: FLUX_CSS.shadowXl,
          borderRadius: "var(--flux-radius-xl)",
          backdropFilter: "blur(4px)",
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true,
      },
      styleOverrides: {
        tooltip: {
          backgroundColor: FLUX_CSS.primary800,
          color: FLUX_HEX.white,
          fontSize: "var(--flux-text-xs)",
          fontWeight: 700,
          borderRadius: "var(--flux-radius-md)",
          padding: "6px 12px",
        },
        arrow: {
          color: FLUX_CSS.primary800,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: "var(--flux-radius-md)",
          border: "none",
        },
        standardSuccess: {
          backgroundColor: alpha(success, 0.12),
          color: FLUX_HEX.textPrimary,
          "& .MuiAlert-icon": { color: success },
          "& .MuiAlertTitle-root": { color: success },
        },
        standardWarning: {
          backgroundColor: alpha(warning, 0.12),
          color: FLUX_HEX.textPrimary,
          "& .MuiAlert-icon": { color: warning },
          "& .MuiAlertTitle-root": { color: warning },
        },
        standardError: {
          backgroundColor: alpha(error, 0.12),
          color: FLUX_HEX.textPrimary,
          "& .MuiAlert-icon": { color: error },
          "& .MuiAlertTitle-root": { color: error },
        },
        standardInfo: {
          backgroundColor: alpha(info, 0.12),
          color: FLUX_HEX.textPrimary,
          "& .MuiAlert-icon": { color: primary300 },
          "& .MuiAlertTitle-root": { color: primary300 },
        },
        outlinedSuccess: {
          backgroundColor: alpha(success, 0.08),
          color: FLUX_HEX.textPrimary,
          borderColor: alpha(success, 0.3),
          "& .MuiAlert-icon": { color: success },
          "& .MuiAlertTitle-root": { color: success },
        },
        outlinedWarning: {
          backgroundColor: alpha(warning, 0.08),
          color: FLUX_HEX.textPrimary,
          borderColor: alpha(warning, 0.3),
          "& .MuiAlert-icon": { color: warning },
          "& .MuiAlertTitle-root": { color: warning },
        },
        outlinedError: {
          backgroundColor: alpha(error, 0.08),
          color: FLUX_HEX.textPrimary,
          borderColor: alpha(error, 0.3),
          "& .MuiAlert-icon": { color: error },
          "& .MuiAlertTitle-root": { color: error },
        },
        outlinedInfo: {
          backgroundColor: alpha(info, 0.08),
          color: FLUX_HEX.textPrimary,
          borderColor: alpha(info, 0.3),
          "& .MuiAlert-icon": { color: primary300 },
          "& .MuiAlertTitle-root": { color: primary300 },
        },
      },
    },
  },
});

export default theme;
